<script setup>
/* global axios */
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { ref, reactive, computed, watch, onBeforeUnmount, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import SettingsLayout from '../settings/SettingsLayout.vue';
import BaseSettingsHeader from '../settings/components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Banner from 'dashboard/components-next/banner/Banner.vue';
import FinanceRows from './FinanceRows.vue';
import GroupRows from './GroupRows.vue';
import FinanceChart from './FinanceChart.vue';
import SexySmmBalance from './SexySmmBalance.vue';
import SmmOrdersPanel from './SmmOrdersPanel.vue';
import { money, kinds, today, formPayload } from './model';
const route = useRoute();
const router = useRouter();
const tabs = [
  { key: 'overview', label: 'Обзор' },
  { key: 'income', label: 'Приходы' },
  { key: 'expense', label: 'Расходы' },
];
const tab = computed(() =>
  ['income', 'expense'].includes(route.query.view)
    ? route.query.view
    : 'overview'
);
const groups = ref([]);
const overview = ref(null);
const entries = ref({ items: [], total: 0, page: 1, total_pages: 1 });
const wallets = ref({ items: [], unu: {} });
const loading = ref(false);
const denied = ref(false);
const error = ref('');
const notice = ref('');
const saving = ref(false);
const uncertain = ref(false);
const formError = ref('');
const from = ref('');
const to = ref('');
const currency = ref('RUB');
const selected = ref([]);
const draftGroups = ref([]);
const groupSearch = ref('');
const entryDialog = ref(null);
const groupDialog = ref(null);
const detailsDialog = ref(null);
const actionDialog = ref(null);
const walletDialog = ref(null);
const target = ref(null);
const action = ref('');
const reason = ref('');
const confirmDate = ref(today());
const walletName = ref('');
const walletKind = ref('provider');
const mode = ref('create');
const correctionTarget = ref(null);
const form = reactive({
  kind: 'expense',
  amount: '',
  date: today(),
  currency: 'RUB',
  category: '',
  description: '',
  wallet: '',
  target: '',
  groupIds: [],
  allocationMode: 'equal',
  shares: {},
  userId: '',
  original: '',
});
let sequence = 0;
let pending = null;
let requests = new AbortController();
const api = () => `/api/v1/accounts/${route.params.accountId}/tg_finance`;
const groupLabel = computed(() =>
  selected.value.length
    ? `Выбрано групп: ${selected.value.length}`
    : 'Все группы'
);
const visibleGroups = computed(() =>
  groups.value.filter(g =>
    g.title.toLocaleLowerCase().includes(groupSearch.value.toLocaleLowerCase())
  )
);
const accountId = computed(() => String(route.params.accountId));
const sameCurrencyWallets = computed(() =>
  wallets.value.items
    .filter(w => w.currency === form.currency)
    .map(w => ({ value: String(w.id), label: w.name }))
);
const formKinds = computed(() =>
  Object.entries(kinds)
    .filter(([key]) => key !== 'reversal')
    .map(([value, label]) => ({ value, label }))
);
const errors = {
  invalid_allocations: 'Проверьте распределение суммы между группами.',
  future_date: 'Для фактической операции нельзя выбрать будущую дату.',
  opening_exists: 'Начальный остаток уже внесён. Исправьте исходную запись.',
  refund_exceeds_original: 'Возврат превышает невозвращённую сумму оплаты.',
  refund_group_exceeded: 'Возврат превышает остаток оплаты выбранной группы.',
  source_changed: 'Данные источника изменились — сначала нужна сверка.',
  already_reviewed: 'Эта запись уже подтверждена или её источник изменился.',
  linked_refunds_exist:
    'У операции есть возвраты. Сначала проверьте связанные записи.',
  provider_fact_read_only:
    'Подтверждённые расходы сервиса исправляются сверкой с провайдером.',
  invalid_date: 'Проверьте дату.',
  invalid_period: 'Выберите период не длиннее 367 дней.',
  request_key_conflict:
    'Параметры уже отправленного запроса отличаются. Проверьте сохранённую запись.',
  report_timeout: 'Отчёт не успел загрузиться. Сократите период и повторите.',
};
function message(e) {
  return (
    errors[e?.response?.data?.error] ||
    'Операция не выполнена. Проверьте поля или повторите запрос.'
  );
}
function query(extra = {}) {
  const q = new URLSearchParams();
  Object.entries({
    from: route.query.from,
    to: route.query.to,
    currency: route.query.currency || 'RUB',
    groups: route.query.groups,
    view: tab.value,
    page: route.query.page || '1',
    status: route.query.status,
    history: route.query.history,
    ...extra,
  }).forEach(([k, v]) => {
    if (v) q.set(k, String(v));
  });
  return q.toString();
}
async function load() {
  sequence += 1;
  const stamp = sequence;
  const owner = accountId.value;
  requests.abort();
  requests = new AbortController();
  loading.value = true;
  error.value = '';
  denied.value = false;
  const base = api();
  const config = { method: 'get', signal: requests.signal };
  try {
    const [boot, report, list, balances] = await Promise.all([
      axios({ ...config, url: `${base}/bootstrap` }),
      axios({ ...config, url: `${base}/overview?${query()}` }),
      axios({
        ...config,
        url: `${base}/entries?${query({ view: route.query.ledger === 'wallets' ? 'wallets' : tab.value })}`,
      }),
      axios({ ...config, url: `${base}/wallets` }),
    ]);
    if (stamp !== sequence || owner !== accountId.value) return;
    groups.value = boot.data.groups;
    overview.value = report.data;
    entries.value = list.data;
    wallets.value = balances.data;
    from.value = route.query.from || report.data.from;
    to.value = route.query.to || report.data.to;
    currency.value = route.query.currency || 'RUB';
    selected.value = String(route.query.groups || '')
      .split(',')
      .filter(Boolean)
      .map(Number);
  } catch (e) {
    if (stamp !== sequence || owner !== accountId.value) return;
    denied.value = [401, 403].includes(e?.response?.status);
    error.value = message(e);
    overview.value = null;
    entries.value = { items: [], total: 0, page: 1, total_pages: 1 };
  } finally {
    if (stamp === sequence) loading.value = false;
  }
}
function navigate(values) {
  router.push({
    name: 'telegram_finance',
    params: route.params,
    query: { ...route.query, ...values },
  });
}
function applyFilters() {
  navigate({
    from: from.value,
    to: to.value,
    currency: currency.value,
    groups: selected.value.join(',') || undefined,
    page: undefined,
  });
}
function switchTab(value) {
  navigate({
    view: value.key,
    page: undefined,
    status: undefined,
    ledger: undefined,
  });
}
function openGroups() {
  draftGroups.value = [...selected.value];
  groupSearch.value = '';
  groupDialog.value?.open();
}
function chooseGroups() {
  selected.value = [...draftGroups.value];
  groupDialog.value?.close();
  applyFilters();
}
function selectGroup(id) {
  selected.value = [id];
  applyFilters();
}
function openForm(kind, row = null) {
  if (pending) {
    entryDialog.value?.open();
    return;
  }
  mode.value = 'create';
  correctionTarget.value = null;
  formError.value = '';
  uncertain.value = false;
  Object.assign(form, {
    kind,
    amount: '',
    date: today(),
    currency: currency.value,
    category:
      { income: 'Размещения', transfer: 'Пополнение сервиса' }[kind] ||
      'Прочее',
    description: '',
    wallet: '',
    target: '',
    groupIds: [...selected.value],
    allocationMode: 'equal',
    shares: {},
    userId: '',
    original: '',
  });
  form.wallet = String(
    wallets.value.items.find(
      w => w.currency === form.currency && w.kind === 'cash'
    )?.id || ''
  );
  if (row) {
    form.amount = (Number(row.amount_minor) / 100).toFixed(2);
    form.currency = row.currency;
    form.description = row.description;
    form.category = row.category;
    form.wallet = String(row.wallet_id);
    form.target = row.target_wallet_id ? String(row.target_wallet_id) : '';
    form.userId = row.telegram_user_id ? String(row.telegram_user_id) : '';
    form.groupIds = row.allocations
      .filter(a => a.group_entry_id)
      .map(a => a.group_entry_id);
    form.shares = Object.fromEntries(
      row.allocations.map(a => [
        a.group_entry_id,
        (Number(a.amount_minor) / 100).toFixed(2),
      ])
    );
    form.allocationMode = form.groupIds.length ? 'manual' : 'equal';
  }
  detailsDialog.value?.close();
  nextTick(() => entryDialog.value?.open());
}
function showDetails(row) {
  target.value = row;
  detailsDialog.value?.open();
}
function startAction(value) {
  action.value = value;
  reason.value = '';
  confirmDate.value = target.value.occurred_on || today();
  formError.value = '';
  detailsDialog.value?.close();
  nextTick(() => actionDialog.value?.open());
}
function startCorrection() {
  const row = target.value;
  openForm(row.kind, row);
  form.date = row.occurred_on;
  mode.value = 'correct';
  correctionTarget.value = row.id;
  reason.value = '';
}
function startRefund() {
  const row = target.value;
  openForm(row.kind === 'income' ? 'income_refund' : 'expense_refund', row);
  form.original = String(row.id);
  form.description = `Возврат · ${row.description}`;
}
async function send(path, payload, dialog) {
  if (saving.value) return;
  const owner = accountId.value;
  if (!pending)
    pending = {
      url: api() + path,
      data: { ...payload, request_key: crypto.randomUUID() },
      owner,
    };
  if (pending.owner !== owner) return;
  saving.value = true;
  formError.value = '';
  try {
    await axios({ method: 'post', url: pending.url, data: pending.data });
    if (owner !== accountId.value) return;
    pending = null;
    uncertain.value = false;
    notice.value = 'Операция сохранена.';
    dialog?.close();
    await load();
  } catch (e) {
    if (owner !== accountId.value) return;
    if (e?.response?.status >= 400 && e.response.status < 500) {
      pending = null;
      uncertain.value = false;
      formError.value = message(e);
    } else {
      uncertain.value = true;
      formError.value =
        'Ответ не получен. Повторите проверку: будет отправлен тот же запрос, без второй записи.';
    }
  } finally {
    if (owner === accountId.value) saving.value = false;
  }
}
async function submit() {
  if (pending) return send('', {}, entryDialog.value);
  try {
    const value = formPayload(form);
    if (mode.value === 'correct') {
      if (!reason.value.trim()) throw new Error('Укажите причину исправления.');
      return send(
        `/entries/${correctionTarget.value}/correct`,
        { replacement: value, reason: reason.value },
        entryDialog.value
      );
    }
    return send('/entries', value, entryDialog.value);
  } catch (e) {
    formError.value = e.message;
    return undefined;
  }
}
function confirmAction() {
  if (!pending && action.value !== 'confirm' && !reason.value.trim()) {
    formError.value = 'Укажите причину действия.';
    return;
  }
  send(
    `/entries/${target.value.id}/${action.value}`,
    action.value === 'confirm'
      ? { occurred_on: confirmDate.value }
      : { reason: reason.value },
    actionDialog.value
  );
}
function createWallet() {
  send(
    '/wallets',
    {
      name: walletName.value,
      currency: currency.value,
      kind: walletKind.value,
    },
    walletDialog.value
  );
}
watch(() => route.fullPath, load, { immediate: true });
watch(accountId, () => {
  pending = null;
  uncertain.value = false;
  saving.value = false;
  notice.value = '';
  target.value = null;
  [entryDialog, actionDialog, detailsDialog, walletDialog].forEach(d =>
    d.value?.close()
  );
  groups.value = [];
  wallets.value = { items: [], unu: {} };
});
watch(
  () => form.currency,
  () => {
    if (!sameCurrencyWallets.value.some(w => w.value === form.wallet))
      form.wallet = sameCurrencyWallets.value[0]?.value || '';
    form.target = '';
  }
);
onBeforeUnmount(() => {
  sequence += 1;
  requests.abort();
});
</script>

<template>
  <SettingsLayout class="min-w-0">
    <template #header>
      <BaseSettingsHeader
        title="Учёт и аналитика"
        description="Деньги, расходы на развитие и показатели Telegram-групп"
      />
      <TabBar
        :tabs="tabs"
        :initial-active-tab="tabs.findIndex(t => t.key === tab)"
        @tab-changed="switchTab"
      />
    </template>
    <template #body>
      <Banner v-if="denied" color="ruby" role="alert">
        Раздел доступен только администратору рабочей области.
      </Banner>
      <div v-else class="flex flex-col gap-5 min-w-0">
        <Banner v-if="notice" color="teal" role="status">{{ notice }}</Banner>
        <Banner v-if="uncertain" color="amber" role="alert">
          Результат сохранения неизвестен. Сначала проверьте прежний
          запрос.<Button
            label="Проверить сохранение"
            :disabled="saving"
            @click="send('', {}, entryDialog)"
          />
        </Banner>
        <form
          class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3 items-end"
          @submit.prevent="applyFilters"
        >
          <Input
            v-model="from"
            type="date"
            label="Период с"
            :disabled="loading || saving"
          />
          <Input
            v-model="to"
            type="date"
            label="По"
            :disabled="loading || saving"
          />
          <div class="flex flex-col gap-2">
            <span class="text-sm font-medium text-n-slate-12">Группы</span
            ><Button
              :label="groupLabel"
              type="button"
              slate
              faded
              :disabled="loading || saving"
              @click="openGroups"
            />
          </div>
          <div class="flex flex-col gap-2">
            <span class="text-sm font-medium text-n-slate-12">Валюта</span
            ><ComboBox
              v-model="currency"
              :options="[
                { value: 'RUB', label: 'Рубли · RUB' },
                { value: 'USD', label: 'Доллары · USD' },
              ]"
              :disabled="loading || saving"
              aria-label="Валюта отчёта"
            />
          </div>
          <Button
            type="submit"
            label="Применить"
            :disabled="loading || saving"
          />
        </form>
        <Banner v-if="error" color="ruby" role="alert">
          {{ error
          }}<Button
            label="Повторить загрузку"
            :disabled="loading"
            @click="load"
          />
        </Banner>
        <p v-if="loading" role="status" class="text-sm text-n-slate-11">
          Загрузка показателей…
        </p>
        <template v-else-if="overview">
          <p class="text-sm text-n-slate-11 m-0">
            {{
              selected.length
                ? 'Учитываются только доли выбранных групп. Общие расходы проекта исключены.'
                : 'Все группы и общие операции проекта. Участники разных групп могут пересекаться.'
            }}
            Валюты не складываются и не пересчитываются автоматически.
          </p>
          <Banner v-if="overview.undated_payments" color="amber">
            Оплаты без даты: {{ overview.undated_payments }} на
            {{ money(overview.undated_income_minor, currency) }}. Они видны в
            приходах, но не включены в итоги и графики выбранного периода.
            <Button
              label="Посмотреть оплаты без даты"
              slate
              ghost
              @click="
                navigate({ view: 'income', status: 'undated', page: undefined })
              "
            />
          </Banner>
          <Banner
            v-if="overview.review_count || overview.changed_sources"
            color="amber"
          >
            Исторических сумм на проверке: {{ overview.review_count }}.
            Изменённых источников: {{ overview.changed_sources }}. Они требуют
            сверки; неподтверждённые суммы не включены в итог.
            <Button
              label="Проверить приходы"
              slate
              ghost
              @click="
                navigate({ view: 'income', status: 'review', page: undefined })
              "
            />
          </Banner>
          <template v-if="tab === 'overview'">
            <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
              <article
                v-for="metric in [
                  {
                    label: 'Чистые поступления',
                    value: money(overview.income_minor, currency),
                    view: 'income',
                  },
                  {
                    label: 'Расходы за вычетом возвратов',
                    value: money(overview.expense_minor, currency),
                    view: 'expense',
                  },
                  {
                    label: 'Результат по учтённым затратам',
                    value: money(overview.result_minor, currency),
                    view: 'overview',
                  },
                  {
                    label: 'Подтверждённых оплат',
                    value: overview.payments,
                    view: 'income',
                  },
                ]"
                :key="metric.label"
                class="p-4 rounded-xl border border-n-weak min-w-0"
              >
                <Button
                  :label="metric.label"
                  slate
                  ghost
                  class="text-left !whitespace-normal"
                  @click="
                    navigate({
                      view: metric.view,
                      status: undefined,
                      page: undefined,
                    })
                  "
                />
                <p
                  class="text-xl font-medium text-n-slate-12 mt-3 mb-0 break-words"
                >
                  {{ metric.value }}
                </p>
              </article>
            </div>
            <p class="text-sm text-n-slate-11 m-0">
              Платящих клиентов с подтверждённым ID:
              {{ overview.paying_clients }}. Повторных оплат по известной
              истории проекта: {{ overview.repeated_payments }}. Оплат без
              подтверждённого клиента:
              {{ overview.unidentified_payments || 0 }}. Это управленческий
              учёт, не бухгалтерская прибыль.
            </p>
            <Banner
              v-if="
                overview.smm_attention?.review ||
                overview.smm_attention?.errors ||
                overview.smm_attention?.stale
              "
              color="amber"
            >
              Часть заказов SexySMM требует сверки или свежей проверки.
              Доходность показана по уже учтённым расходам. Проверьте заказы во
              вкладке «Расходы».
            </Banner>
            <FinanceChart v-if="overview.daily.length" :rows="overview.daily" />
            <p v-else class="text-sm text-n-slate-11">
              За выбранный период подтверждённых денежных операций нет. Добавьте
              приход или расход.
            </p>
            <div class="flex flex-wrap gap-2">
              <Button
                label="Добавить приход"
                @click="openForm('income')"
              /><Button
                label="Добавить расход"
                slate
                faded
                @click="openForm('expense')"
              />
            </div>
            <h2 class="text-lg font-medium text-n-slate-12 m-0">
              Сравнение групп
            </h2>
            <GroupRows
              :rows="overview.groups"
              :currency="currency"
              @select="selectGroup"
            />
            <p v-if="!overview.groups.length" class="text-sm text-n-slate-11">
              Подключённых групп пока нет.
            </p>
            <template v-if="selected.length === 1">
              <FinanceChart
                v-if="overview.groups[0]?.snapshots.length > 1"
                :rows="overview.groups[0].snapshots"
                mode="members"
              />
              <p v-else class="text-sm text-n-slate-11">
                История участников начнёт строиться по мере новых замеров. Для
                графика нужны хотя бы два дня наблюдений.
              </p>
              <p
                v-if="overview.groups[0]?.checked_at"
                class="text-sm text-n-slate-11 m-0"
              >
                Последний замер:
                {{
                  new Date(overview.groups[0].checked_at).toLocaleString(
                    'ru-RU',
                    { timeZone: 'Europe/Moscow' }
                  )
                }}
                МСК. Изменение — чистая разница участников, не отдельный подсчёт
                входов и выходов.
              </p>
            </template>
            <p v-if="!selected.length" class="text-sm text-n-slate-11 m-0">
              Общие расходы проекта:
              {{ money(overview.common_expense_minor, currency) }}. Нажмите
              группу для её графика и детализации.
            </p>
          </template>
          <template v-else>
            <div class="flex flex-wrap gap-2 items-center">
              <Button
                :label="
                  tab === 'income' ? 'Добавить приход' : 'Добавить расход'
                "
                :disabled="saving || uncertain"
                @click="openForm(tab)"
              />
              <Button
                v-if="tab === 'income'"
                :label="
                  route.query.status === 'undated' ? 'Все приходы' : 'Без даты'
                "
                slate
                faded
                @click="
                  navigate({
                    status:
                      route.query.status === 'undated' ? undefined : 'undated',
                    page: undefined,
                  })
                "
              />
              <Button
                v-if="tab === 'expense'"
                label="Пополнить баланс сервиса"
                slate
                faded
                :disabled="saving || uncertain"
                @click="openForm('transfer')"
              />
              <Button
                v-if="tab === 'expense'"
                :label="
                  route.query.ledger === 'wallets'
                    ? 'Показать расходы'
                    : 'Переводы и остатки'
                "
                slate
                ghost
                @click="
                  navigate({
                    ledger:
                      route.query.ledger === 'wallets' ? undefined : 'wallets',
                    page: undefined,
                  })
                "
              />
              <Button
                :label="
                  route.query.history === '1'
                    ? 'Скрыть отменённые'
                    : 'Показать историю отмен'
                "
                slate
                ghost
                @click="
                  navigate({
                    history: route.query.history === '1' ? undefined : '1',
                    page: undefined,
                  })
                "
              />
            </div>
            <Banner v-if="tab === 'income'" color="slate">
              Оплаты из размещений учитываются автоматически. В списке показаны
              оплаты за выбранный период и все оплаты без даты. Неизвестная дата
              отмечена отдельно; такие суммы не входят в показатели периода.
            </Banner>
            <FinanceRows
              :rows="entries.items"
              :disabled="saving"
              @details="showDetails"
            />
            <p v-if="!entries.items.length" class="text-sm text-n-slate-11">
              По выбранным фильтрам записей нет.
            </p>
            <div class="flex flex-wrap items-center gap-3">
              <Button
                label="Назад"
                slate
                faded
                :disabled="entries.page <= 1"
                @click="navigate({ page: String(entries.page - 1) })"
              /><span class="text-sm text-n-slate-11"
                >Страница {{ entries.page }} из {{ entries.total_pages }} ·
                Записей: {{ entries.total }}</span
              ><Button
                label="Далее"
                slate
                faded
                :disabled="entries.page >= entries.total_pages"
                @click="navigate({ page: String(entries.page + 1) })"
              />
            </div>
            <template v-if="tab === 'expense'">
              <h2 class="text-lg font-medium text-n-slate-12 m-0">
                Счета и балансы сервисов
              </h2>
              <p class="text-sm text-n-slate-11 m-0">
                Балансы всего проекта, независимо от фильтров периода и групп.
                Переводы между счетами не являются затратами на услуги.
              </p>
              <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                <article
                  v-for="wallet in wallets.items"
                  :key="wallet.id"
                  class="p-4 rounded-xl border border-n-weak min-w-0"
                >
                  <h3
                    class="text-sm font-medium text-n-slate-12 m-0 break-words"
                  >
                    Движение по журналу
                  </h3>
                  <p class="text-sm text-n-slate-11 m-0 break-words">
                    {{ wallet.name }}
                  </p>
                  <p class="text-lg text-n-slate-12 my-3">
                    {{ money(wallet.balance_minor, wallet.currency) }}
                  </p>
                  <p
                    v-if="!wallet.opening_recorded"
                    class="text-sm text-n-slate-11"
                  >
                    Начальный остаток не внесён: это движение по известным
                    операциям, не сверенный полный баланс.
                  </p>
                  <Button
                    v-if="!wallet.opening_recorded"
                    label="Внести начальный остаток"
                    slate
                    ghost
                    :disabled="uncertain"
                    @click="
                      openForm('opening');
                      form.wallet = String(wallet.id);
                      form.currency = wallet.currency;
                    "
                  />
                </article>
              </div>
              <SmmOrdersPanel
                :account-id="accountId"
                :groups="groups"
                :selected-groups="selected"
                :from="from"
                :to="to"
                :disabled="saving || uncertain"
                @changed="load"
              />
              <SexySmmBalance
                :account-id="accountId"
                :provider="wallets.sexy_smm || {}"
                :disabled="saving || uncertain"
                @updated="wallets.sexy_smm = $event"
                @configured="load"
              />
              <Button
                label="Добавить счёт сервиса"
                slate
                faded
                :disabled="uncertain"
                @click="
                  walletName = '';
                  formError = '';
                  walletDialog?.open();
                "
              />
              <Banner v-if="wallets.unu.error_code" color="amber">
                Данные расходов UNU требуют сверки. Неподтверждённые списания не
                добавляются в итог автоматически.
              </Banner>
              <p
                v-if="wallets.unu.checked_at"
                class="text-sm text-n-slate-11 m-0"
              >
                По API UNU: баланс {{ wallets.unu.balance }} ₽, заморожено
                {{ wallets.unu.blocked }} ₽. Проверено
                {{
                  new Date(wallets.unu.checked_at).toLocaleString('ru-RU', {
                    timeZone: 'Europe/Moscow',
                  })
                }}
                МСК. Журнал пополнений сверяется отдельно.
              </p>
            </template>
          </template>
        </template>
      </div>
      <Dialog
        ref="groupDialog"
        title="Выбор групп"
        confirm-button-label="Применить"
        cancel-button-label="Отмена"
        overflow-y-auto
        @confirm="chooseGroups"
      >
        <Input v-model="groupSearch" label="Найти группу" />
        <div class="flex flex-wrap gap-2">
          <Button
            label="Все группы"
            slate
            faded
            @click="draftGroups = []"
          /><Button
            label="Выбрать все из списка"
            slate
            ghost
            @click="draftGroups = groups.map(g => g.id)"
          />
        </div>
        <p class="text-sm text-n-slate-11">
          Пустой выбор означает все группы вместе с общими операциями проекта.
        </p>
        <label
          v-for="g in visibleGroups"
          :key="g.id"
          class="flex gap-3 items-start text-sm text-n-slate-12 break-words"
          ><input
            v-model="draftGroups"
            type="checkbox"
            :value="g.id"
            class="mt-1"
          />{{ g.title }}</label
        >
      </Dialog>
      <Dialog
        ref="entryDialog"
        :title="mode === 'correct' ? 'Исправление операции' : 'Новая операция'"
        :confirm-button-label="uncertain ? 'Проверить сохранение' : 'Сохранить'"
        cancel-button-label="Закрыть"
        overflow-y-auto
        :is-loading="saving"
        @confirm="submit"
      >
        <fieldset
          :disabled="saving || uncertain"
          class="flex flex-col gap-4 border-0 m-0 p-0 min-w-0"
        >
          <div>
            <span class="text-sm font-medium text-n-slate-12">Тип операции</span
            ><ComboBox
              v-model="form.kind"
              :options="formKinds"
              :disabled="Boolean(form.original)"
              aria-label="Тип операции"
            />
          </div>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input
              v-model="form.amount"
              label="Сумма"
              inputmode="decimal"
              placeholder="2000,00"
            /><Input v-model="form.date" label="Дата операции" type="date" />
          </div>
          <div>
            <span class="text-sm font-medium text-n-slate-12">Валюта</span
            ><ComboBox
              v-model="form.currency"
              :options="[
                { value: 'RUB', label: 'Рубли · RUB' },
                { value: 'USD', label: 'Доллары · USD' },
              ]"
              aria-label="Валюта операции"
            />
          </div>
          <div>
            <span class="text-sm font-medium text-n-slate-12">{{
              form.kind === 'income' ? 'Счёт зачисления' : 'Счёт операции'
            }}</span
            ><ComboBox
              v-model="form.wallet"
              :options="sameCurrencyWallets"
              placeholder="Выберите счёт"
              aria-label="Счёт операции"
            />
          </div>
          <div v-if="form.kind === 'transfer'">
            <span class="text-sm font-medium text-n-slate-12"
              >Куда перевести</span
            ><ComboBox
              v-model="form.target"
              :options="
                sameCurrencyWallets.filter(w => w.value !== form.wallet)
              "
              placeholder="Выберите счёт"
              aria-label="Куда перевести"
            />
            <p class="text-sm text-n-slate-11">
              Пополнение сохраняется один раз. Расходом группы станет
              подтверждённая стоимость услуг.
            </p>
          </div>
          <template v-if="!['transfer', 'opening'].includes(form.kind)">
            <span class="text-sm font-medium text-n-slate-12"
              >Отнести к группам</span
            >
            <p class="text-sm text-n-slate-11 m-0">
              Если группы не выбраны — общая операция проекта.
            </p>
            <div class="max-h-40 overflow-y-auto flex flex-col gap-2">
              <label
                v-for="g in groups"
                :key="g.id"
                class="flex items-start gap-3 text-sm text-n-slate-12 break-words"
                ><input
                  v-model="form.groupIds"
                  type="checkbox"
                  :value="g.id"
                  class="mt-1"
                />{{ g.title }}</label
              >
            </div>
            <div v-if="form.groupIds.length > 1">
              <ComboBox
                v-model="form.allocationMode"
                :options="[
                  { value: 'equal', label: 'Разделить поровну' },
                  { value: 'manual', label: 'Указать доли вручную' },
                ]"
                aria-label="Распределение суммы"
              />
            </div>
            <template v-if="form.allocationMode === 'manual'">
              <Input
                v-for="id in form.groupIds"
                :key="id"
                v-model="form.shares[id]"
                :label="groups.find(g => g.id === id)?.title || String(id)"
                inputmode="decimal"
              />
            </template>
          </template>
          <Input
            v-model="form.category"
            label="Категория"
            placeholder="Реклама, комментарии UNU, прокси…"
          />
          <TextArea
            v-model="form.description"
            aria-label="Назначение / примечание"
            label="Назначение / примечание"
            :max-length="1000"
          />
          <Input
            v-if="['income', 'income_refund'].includes(form.kind)"
            v-model="form.userId"
            label="Telegram ID клиента — если подтверждён"
            inputmode="numeric"
          />
          <Input
            v-if="['income_refund', 'expense_refund'].includes(form.kind)"
            v-model="form.original"
            label="Номер исходной операции"
            inputmode="numeric"
          />
          <TextArea
            v-if="mode === 'correct'"
            v-model="reason"
            label="Причина исправления"
          />
        </fieldset>
        <Banner v-if="formError" color="ruby" role="alert">
          {{ formError }}
        </Banner>
      </Dialog>
      <Dialog
        ref="detailsDialog"
        title="Денежная операция"
        :show-confirm-button="false"
        cancel-button-label="Закрыть"
        overflow-y-auto
      >
        <template v-if="target">
          <p class="text-sm text-n-slate-12 break-words">
            №{{ target.id }} · {{ kinds[target.kind] }} ·
            {{ target.occurred_on || 'Дата неизвестна' }}<br />{{
              target.description
            }}<br />Полная сумма:
            {{ money(target.amount_minor, target.currency) }}
          </p>
          <p class="text-sm text-n-slate-11 break-words">
            Источник: {{ target.source_key }}.
            {{
              target.date_basis === 'payment_date_unknown'
                ? 'Дата оплаты в размещении не указана; сумма не включена в показатели периода.'
                : target.date_basis === 'operator_confirmation_date'
                  ? 'Дата подтверждения оператором, не банковской выписки.'
                  : ''
            }}
          </p>
          <p
            v-for="a in target.allocations"
            :key="a.group_entry_id || 'common'"
            class="text-sm text-n-slate-12 break-words"
          >
            {{ a.title }}: {{ money(a.amount_minor, target.currency) }}
          </p>
          <p v-if="target.telegram_user_id" class="text-sm text-n-slate-11">
            Telegram ID клиента: {{ target.telegram_user_id }}
          </p>
          <p class="text-sm text-n-slate-11 break-words">
            Счёт:
            {{
              wallets.items.find(w => w.id === target.wallet_id)?.name ||
              'Не указан'
            }}<template v-if="target.target_wallet_id">
              →
              {{
                wallets.items.find(w => w.id === target.target_wallet_id)
                  ?.name || 'Не указан'
              }}
            </template>
          </p>
          <router-link
            v-if="target.conversation_url"
            :to="target.conversation_url"
            class="text-sm text-n-blue-11 underline"
          >
            Открыть связанный диалог
          </router-link>
          <Banner v-if="target.source_changed" color="amber">
            Источник изменён. Журнал не переписан. Проверьте изменение отдельно.
            <span v-if="target.observed_source?.amount_minor !== undefined">
              Сейчас в источнике:
              {{ money(target.observed_source.amount_minor, target.currency) }},
              дата
              {{ target.observed_source.occurred_on || 'не указана' }}.</span
            >
          </Banner>
          <Banner color="slate">
            Отмена или исправление записи учёта не отменяет доступ к публикациям
            и не переводит деньги клиенту.
          </Banner>
          <div v-if="!uncertain" class="flex flex-wrap gap-2">
            <Button
              v-if="target.source_changed"
              label="Сверено: журнал верен"
              slate
              faded
              @click="startAction('acknowledge')"
            />
            <Button
              v-if="target.status === 'review'"
              label="Не учитывать как отдельную оплату"
              slate
              ghost
              @click="startAction('dismiss')"
            />
            <Button
              v-if="target.status === 'review' && !target.source_changed"
              label="Подтвердить после сверки"
              @click="startAction('confirm')"
            />
            <template
              v-else-if="
                !target.source_key.startsWith('unu:') &&
                !target.source_key.startsWith('smm:') &&
                target.kind !== 'reversal' &&
                route.query.history !== '1'
              "
            >
              <Button
                label="Исправить запись"
                slate
                faded
                @click="startCorrection"
              />
              <Button
                v-if="['income', 'expense'].includes(target.kind)"
                label="Записать возврат"
                slate
                faded
                @click="startRefund"
              />
              <Button
                label="Отменить ошибочную запись"
                ruby
                faded
                @click="startAction('cancel')"
              />
            </template>
          </div>
        </template>
      </Dialog>
      <Dialog
        ref="actionDialog"
        :title="
          action === 'confirm'
            ? 'Подтверждение исторической суммы'
            : 'Проверка записи учёта'
        "
        :confirm-button-label="
          uncertain ? 'Проверить сохранение' : 'Подтвердить'
        "
        cancel-button-label="Закрыть"
        :is-loading="saving"
        @confirm="confirmAction"
      >
        <Input
          v-if="action === 'confirm'"
          v-model="confirmDate"
          type="date"
          label="Проверенная дата получения денег"
          :disabled="saving || uncertain"
        />
        <TextArea
          v-else
          v-model="reason"
          label="Причина действия / результат сверки"
          aria-label="Причина действия"
          :disabled="saving || uncertain"
        />
        <Banner v-if="formError" color="ruby" role="alert">
          {{ formError }}
        </Banner>
      </Dialog>
      <Dialog
        ref="walletDialog"
        title="Новый счёт"
        confirm-button-label="Сохранить"
        cancel-button-label="Закрыть"
        :is-loading="saving"
        @confirm="createWallet"
      >
        <Input
          v-model="walletName"
          label="Название сервиса или счёта"
          :disabled="saving || uncertain"
        />
        <ComboBox
          v-model="walletKind"
          :options="[
            { value: 'provider', label: 'Баланс сервиса' },
            { value: 'cash', label: 'Средства проекта' },
          ]"
          :disabled="saving || uncertain"
          aria-label="Тип счёта"
        />
        <p class="text-sm text-n-slate-11">
          Валюта: {{ currency }}. Это запись учёта, без подключения к банку или
          сервису.
        </p>
        <Banner v-if="formError" color="ruby" role="alert">
          {{ formError }}
        </Banner>
      </Dialog>
    </template>
  </SettingsLayout>
</template>
